import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getBillingLicensePlansQuery,
  getBusinessLicenseQuery,
  getScheduledBusinessLicensesQuery,
  getBillingSettingsQuery,
  getPaymentMethodsQuery,
} from 'query/billing';
import { BusinessLicensePlanAssociation } from '@kizen/api/billing';
import { PageSizing } from 'components/Layout/PageContentWidth';
import { ContentCard, PageWrapper } from '../styles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BillingChangeToPlan,
  BillingChangeToPlanPayload,
  BillingLicensePlans,
} from '@kizen/api/billing';
import { PlanRow } from './components/PlanRow';
import { Spacer } from '@kizen/kds/Spacer';
import { Button } from '@kizen/kds/Button';
import Switch from '__components/Kizen/Switch';
import { useTranslation } from 'react-i18next';
import { Typography } from '@kizen/kds/Typography';
import { Estimator } from './components/Estimator';
import Loader from '__components/Kizen/Loader';
import { Comparison } from './components/Comparison';
import {
  CurrentlyActivePlan,
  useSelectedPlanResolution,
} from './hooks/useSelectedPlanResolution';
import { arePlansEqual, buildPlanChangeClarification } from './util';
import {
  computeCadenceAmounts,
  planDiscount,
} from './components/planSummaryShared';
import { deferExecution } from 'utility/defer';
import BillingService from 'services/BillingService';
import useModal from '__components/Modals/useModal';
import { useToast } from '__components/ToastProvider';
import { toastVariant } from '__components/ToastProvider';
import ConfirmNavigationModal from '__components/Modals/presets/ConfirmNavigation';
import { UserCountErrorDialog } from './components/Dialogs/UserCountErrorDialog';
import { PendingPlanChangesDialog } from './components/Dialogs/PendingPlanChanges';
import { DowngradeCancellationDialog } from './components/Dialogs/DowngradeCancellationDialog';
import { ConfirmPlanChangesDialog } from './components/Dialogs/ConfirmPlanChangesDialog';
import { CannotChangeBillingCadenceDialog } from './components/Dialogs/CannotChangeBillingCadence';
import { PlanSummary, BillingCadence } from './components/PlanSummary';
import { MissingBillingDetailsDialog } from './components/Dialogs/MissingBillingDetails';
import { useHistory } from 'react-router-dom';
import { RequiresPaidPlanToCancelDowngrade } from './components/Dialogs/RequiresPaidPlanToCancelDowngrade';
import { usePreReleaseFeatures } from 'hooks/usePreReleaseFeatures';
import { useDispatch } from 'react-redux';
import { refreshBootstrapBusiness } from 'store/authentication/authenticationAction';
import { useBillingPermissions } from 'ts-components/hooks/permissions/billing';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { AddCardModal } from '../DetailsPage/components/AddCardModal';

export const PlanPage = () => {
  const { t } = useTranslation();
  const showAnnualBilling = usePreReleaseFeatures();
  const [showComparisonTable, setShowComparisonTable] = useState(false);
  const generalPlanRowRef = useRef<HTMLDivElement>(null);
  const [billingPreviewResponse, setBillingPreviewResponse] =
    useState<BillingChangeToPlan | null>(null);
  const [previewedCadence, setPreviewedCadence] =
    useState<BillingCadence | null>(null);
  const [pendingRecalculation, setPendingRecalculation] = useState(false);
  const previewRequestSeq = useRef(0);
  const hasShownConfirmedUpcomingRef = useRef(false);
  const history = useHistory();

  const [showToast] = useToast();

  // License templates are available plans to choose from
  const { data: licenseTemplates, isLoading: licenseTemplatesLoading } =
    useQuery(getBillingLicensePlansQuery());

  // License plans are the plans that the business has active before making any changes
  const {
    data: licensePlans,
    isLoading: licensePlansLoading,
    refetch: refetchLicensePlans,
  } = useQuery(getBusinessLicenseQuery());

  // These are downgrades that are pending at a later date
  const {
    data: pendingChanges,
    isLoading: pendingChangesLoading,
    refetch: refetchPendingChanges,
  } = useQuery(getScheduledBusinessLicensesQuery());

  const { data: billingSettings, isLoading: billingSettingsLoading } = useQuery(
    getBillingSettingsQuery()
  );

  /*
    These indices will be null if we should fall back on the existing active plan (i.e. no changes needed).
    If the index is -1, it means the user selected a plan, then reselected the legacy plan they were on.
    In this case, it should be treated the same as if the user never selected a new plan.
    Otherwise, the value will be between 0 and n, where n is the index of the last available plan in the list
    for the particular category.
  */
  const [currentSelectedGeneralPlanIndex, setCurrentSelectedGeneralPlanIndex] =
    useState<number | null>(null);
  const [
    currentSelectedAutomationPlanIndex,
    setCurrentSelectedAutomationPlanIndex,
  ] = useState<number | null>(null);
  const [
    currentSelectedMarketingPlanIndex,
    setCurrentSelectedMarketingPlanIndex,
  ] = useState<number | null>(null);

  /*
    The manually entered user count. Should be null when the user hasn't made an explicit selection
  */
  const [chosenUserCount, setChosenUserCount] = useState<number | null>(null);

  const handleSelectGeneralPlanIndex = useCallback((index: number) => {
    setChosenUserCount(null);
    setCurrentSelectedGeneralPlanIndex(index);
  }, []);

  const resetSelections = useCallback(() => {
    setCurrentSelectedGeneralPlanIndex(null);
    setCurrentSelectedAutomationPlanIndex(null);
    setCurrentSelectedMarketingPlanIndex(null);
    setChosenUserCount(null);
  }, []);

  /* 
    These are the current plans for the business. They don't have to be published, so might not appear
    in the available templates. They should be selectable until the user changes to a published plan,
    at which point there is no longer an active legacy plan.
  */
  const { currentGeneralPlan, currentAutomationPlan, currentMarketingPlan } =
    useMemo(() => {
      let general: CurrentlyActivePlan;
      let automation: CurrentlyActivePlan;
      let marketing: CurrentlyActivePlan;

      licensePlans?.forEach((plan) => {
        if (plan.license_plan.plan_category === 'general') {
          general = plan;
        } else if (plan.license_plan.plan_category === 'automation') {
          automation = plan;
        } else if (plan.license_plan.plan_category === 'email') {
          marketing = plan;
        }
      });

      return {
        currentGeneralPlan: general,
        currentAutomationPlan: automation,
        currentMarketingPlan: marketing,
      };
    }, [licensePlans]);

  const hasFreeAutomationPlan = Boolean(
    currentAutomationPlan?.price.amount === 0
  );
  const hasFreeMarketingPlan = Boolean(
    currentMarketingPlan?.price.amount === 0
  );

  /*
    These are the plans that are pending downgrade for the business
  */
  const { pendingGeneralPlan, pendingAutomationPlan, pendingMarketingPlan } =
    useMemo(() => {
      let general: CurrentlyActivePlan;
      let automation: CurrentlyActivePlan;
      let marketing: CurrentlyActivePlan;

      pendingChanges?.forEach((plan) => {
        if (plan.license_plan.plan_category === 'general') {
          general = plan;
        } else if (plan.license_plan.plan_category === 'automation') {
          automation = plan;
        } else if (plan.license_plan.plan_category === 'email') {
          marketing = plan;
        }
      });

      return {
        pendingGeneralPlan: general,
        pendingAutomationPlan: automation,
        pendingMarketingPlan: marketing,
      };
    }, [pendingChanges]);

  const pendingPlanIds = useMemo(() => {
    return (
      (pendingChanges ?? []) as (BusinessLicensePlanAssociation & {
        id: string;
      })[]
    ).map(({ id }) => id);
  }, [pendingChanges]);

  const hasFreePendingAutomationPlan = Boolean(
    pendingAutomationPlan?.price.amount === 0
  );

  const hasFreePendingMarketingPlan = Boolean(
    pendingMarketingPlan?.price.amount === 0
  );

  const hasAnyPendingPlans = Boolean(
    pendingGeneralPlan || pendingAutomationPlan || pendingMarketingPlan
  );

  const pendingCadence: 'month' | 'year' =
    (
      (pendingGeneralPlan ?? pendingAutomationPlan ?? pendingMarketingPlan) as
        | { billing_cycle?: string }
        | undefined
    )?.billing_cycle === 'year'
      ? 'year'
      : 'month';

  // These are the plans that are currently published and selectable as a plan change
  const { corePlatformPlans, automationsPlans, marketingPlans } =
    useMemo(() => {
      const core: BillingLicensePlans['license_templates'] = [];
      const automations: BillingLicensePlans['license_templates'] = [];
      const marketing: BillingLicensePlans['license_templates'] = [];

      licenseTemplates?.license_templates?.forEach((plan) => {
        if (plan.plan_category === 'general') {
          core.push(plan);
        } else if (plan.plan_category === 'automation') {
          automations.push(plan);
        } else if (plan.plan_category === 'email') {
          marketing.push(plan);
        }
      });

      return {
        corePlatformPlans: core.sort((a, b) => a.tier_order - b.tier_order),
        automationsPlans: automations.sort(
          (a, b) => a.tier_order - b.tier_order
        ),
        marketingPlans: marketing.sort((a, b) => a.tier_order - b.tier_order),
      };
    }, [licenseTemplates]);

  const isLoading =
    licenseTemplatesLoading ||
    licensePlansLoading ||
    pendingChangesLoading ||
    billingSettingsLoading;

  const currentCadenceIsAnnual = currentGeneralPlan?.billing_cycle === 'year';

  const [showAnnualPricing, setShowAnnualPricing] = useState(false);
  const didInitAnnualPricing = useRef(false);

  useEffect(() => {
    if (!didInitAnnualPricing.current && currentGeneralPlan) {
      setShowAnnualPricing(currentCadenceIsAnnual);
      didInitAnnualPricing.current = true;
    }
  }, [currentGeneralPlan, currentCadenceIsAnnual]);

  const currentCadence: 'month' | 'year' = currentCadenceIsAnnual
    ? 'year'
    : 'month';

  // Pre-release safety net: never surface annual pricing/cadence in the UI
  // when the feature is toggled off, regardless of internal state.
  const effectiveShowAnnualPricing = showAnnualBilling && showAnnualPricing;

  const pendingCadenceChanges =
    hasAnyPendingPlans && pendingCadence !== currentCadence;

  const [upcomingCadence, setUpcomingCadence] = useState<'month' | 'year'>(
    'month'
  );
  const upcomingCadenceInitialized = useRef(false);
  const [requestedCadenceChangeOnly, setRequestedCadenceChangeOnly] =
    useState(false);

  // Currently chosen core platform (general) plan in the UI. Could be one of the published plans,
  // or the active legacy plan.
  const {
    active: activeCorePlatformPlan,
    current: currentSelectedCorePlatformPlan,
    isPaid: isCorePlanPaid,
    pending: pendingCorePlatformResolvedPlan,
  } = useSelectedPlanResolution({
    plans: corePlatformPlans,
    currentPlan: currentGeneralPlan,
    currentSelectedPlanIndex: currentSelectedGeneralPlanIndex,
    pendingPlan: pendingGeneralPlan,
  });

  // Currently chosen automation plan in the UI. Could be one of the published plans,
  // or the active legacy plan.
  const {
    active: activeAutomationPlan,
    current: currentSelectedAutomationPlan,
    next: nextSelectedAutomationPlan,
    showEstimator: showAutomationEstimator,
    pending: pendingAutomationResolvedPlan,
  } = useSelectedPlanResolution({
    plans: automationsPlans,
    currentPlan: currentAutomationPlan,
    currentSelectedPlanIndex: currentSelectedAutomationPlanIndex,
    pendingPlan: pendingAutomationPlan,
  });

  const hasFreeSelectedAutomationPlan = Boolean(
    currentSelectedAutomationPlan?.price?.amount === 0
  );

  // Currently chosen marketing (emails) plan in the UI. Could be one of the published plans,
  // or the active legacy plan.
  const {
    active: activeMarketingPlan,
    current: currentSelectedMarketingPlan,
    next: nextSelectedMarketingPlan,
    showEstimator: showMarketingEstimator,
    pending: pendingMarketingResolvedPlan,
  } = useSelectedPlanResolution({
    plans: marketingPlans,
    currentPlan: currentMarketingPlan,
    currentSelectedPlanIndex: currentSelectedMarketingPlanIndex,
    pendingPlan: pendingMarketingPlan,
  });

  const hasFreeSelectedMarketingPlan = Boolean(
    currentSelectedMarketingPlan?.price?.amount === 0
  );

  // If the user has made a selection, use that, otherwise pull the max user count from the plan,
  // which for free plans will be fixed, and for paid plans will be whatever they have previously
  // chosen.
  const numUsersForCalculation =
    chosenUserCount ?? (currentGeneralPlan?.config?.limits as any)?.max_users;

  // The Current summary card must always reflect the business's actual active seat
  // count, not whatever the user is currently editing in the form.
  const currentNumUsers =
    (currentGeneralPlan?.config?.limits as any)?.max_users ?? 0;

  const scrollToGeneralPlanRow = useCallback(() => {
    deferExecution(() => {
      generalPlanRowRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    });
  }, []);

  const isCorePlanDirty = useMemo(() => {
    return !arePlansEqual(currentSelectedCorePlatformPlan, currentGeneralPlan);
  }, [currentGeneralPlan, currentSelectedCorePlatformPlan]);

  const isAutomationPlanDirty = useMemo(() => {
    return !arePlansEqual(currentSelectedAutomationPlan, currentAutomationPlan);
  }, [currentAutomationPlan, currentSelectedAutomationPlan]);

  const isMarketingPlanDirty = useMemo(() => {
    return !arePlansEqual(currentSelectedMarketingPlan, currentMarketingPlan);
  }, [currentMarketingPlan, currentSelectedMarketingPlan]);

  const isUserCountDirty =
    numUsersForCalculation !==
    (currentGeneralPlan?.config?.limits as any)?.max_users;

  const isDirty =
    isCorePlanDirty ||
    isAutomationPlanDirty ||
    isMarketingPlanDirty ||
    isUserCountDirty;

  const currentPreviewUsers = isUserCountDirty ? chosenUserCount : undefined;

  const showUpcoming = isDirty || requestedCadenceChangeOnly;

  const annualDiscountPercentage = billingPreviewResponse
    ? billingPreviewResponse.total_annual_discount_percentage ?? 0
    : billingSettings?.total_annual_discount_percentage ?? 0;

  const planChangeClarification = billingPreviewResponse
    ? buildPlanChangeClarification(billingPreviewResponse, t)
    : undefined;

  if (!showUpcoming) {
    hasShownConfirmedUpcomingRef.current = false;
  } else if (billingPreviewResponse !== null) {
    hasShownConfirmedUpcomingRef.current = true;
  }

  const isAwaitingPreview =
    isDirty &&
    billingPreviewResponse === null &&
    !hasShownConfirmedUpcomingRef.current;

  useEffect(() => {
    if (showUpcoming && !upcomingCadenceInitialized.current) {
      setUpcomingCadence(showAnnualPricing ? 'year' : 'month');
      upcomingCadenceInitialized.current = true;
    }
    if (!showUpcoming) {
      upcomingCadenceInitialized.current = false;
    }
  }, [showUpcoming, showAnnualPricing]);

  const canSwitchCadenceOnly = useMemo(() => {
    if (isDirty || hasAnyPendingPlans) {
      return false;
    }
    const corePaid = (activeCorePlatformPlan?.price?.amount ?? 0) > 0;
    const automationPaid = (activeAutomationPlan?.price?.amount ?? 0) > 0;
    const marketingPaid = (activeMarketingPlan?.price?.amount ?? 0) > 0;
    return corePaid || automationPaid || marketingPaid;
  }, [
    isDirty,
    hasAnyPendingPlans,
    activeCorePlatformPlan,
    activeAutomationPlan,
    activeMarketingPlan,
  ]);

  const handleRequestCadenceChange = useCallback(() => {
    const opposite = currentCadence === 'year' ? 'month' : 'year';
    setUpcomingCadence(opposite);
    setShowAnnualPricing(opposite === 'year');
    upcomingCadenceInitialized.current = true;
    setRequestedCadenceChangeOnly(true);
    setBillingPreviewResponse(null);
  }, [currentCadence]);

  const handleCancelUpdate = useCallback(() => {
    resetSelections();
    setRequestedCadenceChangeOnly(false);
    setShowAnnualPricing(currentCadenceIsAnnual);
    upcomingCadenceInitialized.current = false;
  }, [resetSelections, currentCadenceIsAnnual]);

  const handleUpcomingCadenceChange = useCallback(
    (cadence: 'month' | 'year') => {
      setUpcomingCadence(cadence);
      setShowAnnualPricing(cadence === 'year');
      // Clear immediately so the old cadence's numbers don't linger under the new
      // label while the debounced refetch is in flight.
      setBillingPreviewResponse(null);
      setPreviewedCadence(null);
    },
    []
  );

  const [
    lockedPlanCategoryModalProps,
    ,
    { show: showLockedPlanCategoryModal },
  ] = useModal();

  const [invalidUserCountModalProps, , { show: showInvalidUserCountModal }] =
    useModal({
      handleHide: () => {
        setChosenUserCount(null);
        setCurrentSelectedGeneralPlanIndex(null);
      },
    });
  const [missingBillingInfoModalProps, , { show: showMissingInfoModal }] =
    useModal({
      handleSubmit: () => history.push('/settings/billing/details'),
    });
  const [
    cannotChangeCadenceModalProps,
    ,
    { show: showCannotChangeCadenceModal },
  ] = useModal();

  const handleMaxUsersError = useCallback(() => {
    showInvalidUserCountModal();
  }, [showInvalidUserCountModal]);

  const { canEditPaymentMethods } = useBillingPermissions();
  const [showAddCardModal, setShowAddCardModal] = useState(false);

  const handleMissingBillingDetailsError = useCallback(() => {
    if (canEditPaymentMethods) {
      setShowAddCardModal(true);
    } else {
      resetSelections();
      showMissingInfoModal();
    }
  }, [canEditPaymentMethods, showMissingInfoModal, resetSelections]);

  const handleAddCardModalHide = useCallback(() => {
    setShowAddCardModal(false);
    resetSelections();
  }, [resetSelections]);

  const handleFreeBusinessCadenceError = useCallback(() => {
    setUpcomingCadence(currentCadence);
    showCannotChangeCadenceModal();
  }, [currentCadence, showCannotChangeCadenceModal]);

  const handleTemplatePlanError = useCallback(
    (
      planTypes: Array<{
        isAutomation: boolean;
        isCore: boolean;
        isMarketing: boolean;
      }>
    ) => {
      if (planTypes.some((p) => p.isAutomation)) {
        setCurrentSelectedAutomationPlanIndex(null);
      }
      if (planTypes.some((p) => p.isCore)) {
        setChosenUserCount(null);
        setCurrentSelectedGeneralPlanIndex(null);
      }
      if (planTypes.some((p) => p.isMarketing)) {
        setCurrentSelectedMarketingPlanIndex(null);
      }

      showLockedPlanCategoryModal();
    },
    [showLockedPlanCategoryModal]
  );

  const refreshBillingEstimate = useCallback(
    async (
      templates: Array<string> = [],
      users?: number | null,
      planIds?: { core?: string; automations?: string; marketing?: string },
      cadenceOnly = false
    ) => {
      const payload: BillingChangeToPlanPayload = {};

      let hasTemplates = false;
      let hasUsers = false;

      if (templates.length > 0) {
        hasTemplates = true;
        payload.template_plan_ids = templates;
      }

      if (typeof users === 'number') {
        hasUsers = true;
        payload.max_users = users;
      }

      const nothingToPreview =
        !hasTemplates &&
        !hasUsers &&
        (!cadenceOnly || upcomingCadence === currentCadence);

      if (nothingToPreview) {
        // No plan/seat/cadence change is actually pending, so there's nothing
        // to preview.
        setBillingPreviewResponse(null);
        setPreviewedCadence(null);
        setPendingRecalculation(false);
        return;
      }

      payload.billing_cycle = upcomingCadence;

      const requestId = ++previewRequestSeq.current;
      setPendingRecalculation(true);

      try {
        const estimate = await BillingService.changeToPlan(payload, true);
        if (requestId !== previewRequestSeq.current) {
          return;
        }

        setBillingPreviewResponse(estimate);
        setPreviewedCadence(upcomingCadence);

        if (estimate.update_max_users || estimate.update_max_users === 0) {
          setChosenUserCount(estimate.update_max_users);
        }
      } catch (ex: any) {
        if (requestId !== previewRequestSeq.current) {
          return;
        }
        const planTypes = templates.map((id) => {
          const isAutomation = id === planIds?.automations;
          const isMarketing = id === planIds?.marketing;
          const isCore = id === planIds?.core;

          return {
            isAutomation,
            isMarketing,
            isCore,
          };
        });

        const data = ex.response?.data;
        const errors = data?.errors as any;
        const hasPendingChangesError =
          data?.billing_cycle?.code ===
            'cannot_change_plan_with_pending_changes' ||
          errors?.billing_cycle?.code ===
            'cannot_change_plan_with_pending_changes';
        const isFreeBusinessCadenceError =
          data?.billing_cycle?.code ===
            'cannot_change_billing_cycle_for_free_business' ||
          errors?.billing_cycle?.code ===
            'cannot_change_billing_cycle_for_free_business';

        if (errors?.max_users) {
          handleMaxUsersError();
        } else if (errors?.template_plan_ids || hasPendingChangesError) {
          handleTemplatePlanError(planTypes);
        } else if (isFreeBusinessCadenceError) {
          handleFreeBusinessCadenceError();
        } else if (
          errors?.non_field_errors &&
          errors.non_field_errors.code ===
            'default_payment_method_not_set_on_business'
        ) {
          handleMissingBillingDetailsError();
        } else {
          showToast({
            message:
              ex.response?.data?.message ||
              t(
                'An unexpected error occurred. Please make your selections and try again.'
              ),
            variant: toastVariant.FAILURE,
          });
          resetSelections();
          setRequestedCadenceChangeOnly(false);
        }
      } finally {
        if (requestId === previewRequestSeq.current) {
          setPendingRecalculation(false);
        }
      }
    },
    [
      resetSelections,
      handleMaxUsersError,
      handleTemplatePlanError,
      showToast,
      t,
      handleMissingBillingDetailsError,
      handleFreeBusinessCadenceError,
      upcomingCadence,
      currentCadence,
    ]
  );

  const [
    cannotCancelDowngradeModalProps,
    ,
    { show: showCannotCancelDowngradeModal },
  ] = useModal();

  const handleCancelPendingDowngrade = useCallback(
    async (ids: Array<string>) => {
      try {
        await BillingService.cancelPendingChange(ids);
        refetchPendingChanges();
        refetchLicensePlans();
      } catch (ex: any) {
        const errors = ex.response?.data?.errors as any;

        if (
          errors?.business_license_ids?.code ===
          'cannot_cancel_scheduled_downgrade_plans_without_also_cancelling_core_platform_downgrade_validation_error'
        ) {
          showCannotCancelDowngradeModal();
        } else if (
          errors?.business_license_ids?.code ===
          'cannot_cancel_scheduled_cadence_change_partially'
        ) {
          showToast({
            message: t(
              'A scheduled billing cycle change must be cancelled for all plans at once. Cancel the pending change from the Upcoming Plan Summary.'
            ),
            variant: toastVariant.FAILURE,
          });
        } else {
          showToast({
            message:
              ex.response?.data?.message ||
              t(
                'An unexpected error occurred. Please try your downgrade again.'
              ),
            variant: toastVariant.FAILURE,
          });
        }
      }
    },
    [
      refetchPendingChanges,
      refetchLicensePlans,
      showToast,
      t,
      showCannotCancelDowngradeModal,
    ]
  );

  const handleCancelPendingAndSwitchCadence = useCallback(async () => {
    const oppositeCadence = pendingCadence === 'year' ? 'month' : 'year';

    const pendingTemplateIds = [
      pendingGeneralPlan ? pendingCorePlatformResolvedPlan?.template_id : null,
      pendingAutomationPlan ? pendingAutomationResolvedPlan?.template_id : null,
      pendingMarketingPlan ? pendingMarketingResolvedPlan?.template_id : null,
    ].filter((id): id is string => Boolean(id));
    const pendingMaxUsers = pendingGeneralPlan
      ? (pendingGeneralPlan?.config?.limits as any)?.max_users
      : undefined;

    setPendingRecalculation(true);
    try {
      await BillingService.cancelPendingChange(pendingPlanIds);
    } catch (ex: any) {
      setPendingRecalculation(false);
      showToast({
        message:
          ex.response?.data?.message ||
          t(
            'We could not cancel the pending plan change. Your billing cycle was not changed. Please try again.'
          ),
        variant: toastVariant.FAILURE,
      });
      return;
    }

    try {
      const payload: BillingChangeToPlanPayload = {
        billing_cycle: oppositeCadence,
      };
      if (pendingTemplateIds.length > 0) {
        payload.template_plan_ids = pendingTemplateIds;
      }
      if (typeof pendingMaxUsers === 'number') {
        payload.max_users = pendingMaxUsers;
      }
      await BillingService.changeToPlan(payload, false);
    } catch (ex: any) {
      showToast({
        message:
          ex.response?.data?.message ||
          t(
            'The pending plan change was cancelled, but switching your billing cycle failed. Please try changing your billing cycle again.'
          ),
        variant: toastVariant.FAILURE,
      });
    } finally {
      // There is a race condition on the backend where changing a plan doesn't immediately cause the new plan
      // to be returned. We need to wait a bit before refetching. This should be a temporary
      // solution but it's what is recommended by the backend team for now.
      await new Promise((resolve) => setTimeout(resolve, 1000));
      resetSelections();
      setRequestedCadenceChangeOnly(false);
      refetchLicensePlans();
      refetchPendingChanges();
      setPendingRecalculation(false);
    }
  }, [
    pendingCadence,
    pendingPlanIds,
    pendingGeneralPlan,
    pendingAutomationPlan,
    pendingMarketingPlan,
    pendingCorePlatformResolvedPlan,
    pendingAutomationResolvedPlan,
    pendingMarketingResolvedPlan,
    showToast,
    t,
    resetSelections,
    refetchLicensePlans,
    refetchPendingChanges,
  ]);

  const pendingOppositeCadence = pendingCadence === 'year' ? 'month' : 'year';

  const [
    confirmPendingCadenceChangeModalProps,
    ,
    { show: showConfirmPendingCadenceChange },
  ] = useModal({
    handleSubmit: handleCancelPendingAndSwitchCadence,
  });

  const commitPlanChanges = useCallback(async () => {
    const templates = [
      isAutomationPlanDirty
        ? currentSelectedAutomationPlan?.template_id ?? ''
        : '',
      isCorePlanDirty ? currentSelectedCorePlatformPlan?.template_id ?? '' : '',
      isMarketingPlanDirty
        ? currentSelectedMarketingPlan?.template_id ?? ''
        : '',
    ].filter(Boolean);

    const users = chosenUserCount;

    const payload: BillingChangeToPlanPayload = {};

    if (templates.length > 0) {
      payload.template_plan_ids = templates;
    }

    if (typeof users === 'number') {
      payload.max_users = users;
    }

    payload.billing_cycle = upcomingCadence;

    await BillingService.changeToPlan(payload, false);
    // There is a race condition on the backend where changing a plan doesn't immediately cause the new plan
    // to be returned. We need to wait a bit before refetching. This should be a temporary
    // solution but it's what is recommended by the backend team for now.
    await new Promise((resolve) => setTimeout(resolve, 1000));

    resetSelections();
    setRequestedCadenceChangeOnly(false);
    refetchLicensePlans();
    refetchPendingChanges();
  }, [
    currentSelectedAutomationPlan,
    currentSelectedCorePlatformPlan,
    currentSelectedMarketingPlan,
    isAutomationPlanDirty,
    isCorePlanDirty,
    isMarketingPlanDirty,
    chosenUserCount,
    upcomingCadence,
    refetchLicensePlans,
    refetchPendingChanges,
    resetSelections,
  ]);

  const refreshBillingEstimateForCurrentState = useCallback(() => {
    return refreshBillingEstimate(
      [
        isAutomationPlanDirty
          ? currentSelectedAutomationPlan?.template_id ?? ''
          : '',
        isCorePlanDirty
          ? currentSelectedCorePlatformPlan?.template_id ?? ''
          : '',
        isMarketingPlanDirty
          ? currentSelectedMarketingPlan?.template_id ?? ''
          : '',
      ].filter(Boolean),
      currentPreviewUsers,
      {
        core: currentSelectedCorePlatformPlan?.template_id,
        automations: currentSelectedAutomationPlan?.template_id,
        marketing: currentSelectedMarketingPlan?.template_id,
      },
      requestedCadenceChangeOnly
    );
  }, [
    isAutomationPlanDirty,
    isCorePlanDirty,
    isMarketingPlanDirty,
    currentPreviewUsers,
    currentSelectedAutomationPlan,
    currentSelectedCorePlatformPlan,
    currentSelectedMarketingPlan,
    refreshBillingEstimate,
    requestedCadenceChangeOnly,
  ]);

  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const handleCardAdded = useCallback(
    async (paymentMethodId: string) => {
      try {
        const { payment_methods } = await queryClient.fetchQuery({
          ...getPaymentMethodsQuery(),
          staleTime: 0,
        });
        await BillingService.setPaymentMethodOrder([
          paymentMethodId,
          ...payment_methods
            .map((method) => method.id)
            .filter((id) => id !== paymentMethodId),
        ]);
      } catch (error) {
        monitoringExceptionHelper(error);
        showToast({
          message: t('Failed to update default payment method'),
          variant: toastVariant.FAILURE,
        });
        handleAddCardModalHide();
        return;
      }

      queryClient.invalidateQueries({
        queryKey: getPaymentMethodsQuery().queryKey,
      });
      dispatch(refreshBootstrapBusiness());
      showToast({
        message: t('Successfully added payment method'),
        variant: toastVariant.SUCCESS,
      });
      setShowAddCardModal(false);
      refreshBillingEstimateForCurrentState();
    },
    [
      queryClient,
      dispatch,
      showToast,
      t,
      handleAddCardModalHide,
      refreshBillingEstimateForCurrentState,
    ]
  );

  const [confirmSaveChangesModalProps, , { show: showConfirmSaveChanges }] =
    useModal({
      handleSubmit: commitPlanChanges,
    });

  const [
    pendingPlanDowngradeCancellations,
    setPendingPlanDowngradeCancellations,
  ] = useState<Array<string>>([]);

  const [cancelDowngradeModalProps, , { show: showConfirmCancelDowngrade }] =
    useModal({
      handleSubmit: () => {
        handleCancelPendingDowngrade(pendingPlanDowngradeCancellations);
        setPendingPlanDowngradeCancellations([]);
      },
    });

  const clickSaveChanges = useCallback(() => {
    showConfirmSaveChanges();
    refreshBillingEstimateForCurrentState();
  }, [showConfirmSaveChanges, refreshBillingEstimateForCurrentState]);

  useEffect(() => {
    refreshBillingEstimateForCurrentState();
  }, [
    currentSelectedCorePlatformPlan?.template_id,
    currentSelectedAutomationPlan?.template_id,
    currentSelectedMarketingPlan?.template_id,
    chosenUserCount,
    upcomingCadence,
    requestedCadenceChangeOnly,
    refreshBillingEstimateForCurrentState,
  ]);

  const willAutomationPlanBeFree =
    hasFreeAutomationPlan ||
    hasFreePendingAutomationPlan ||
    hasFreeSelectedAutomationPlan;

  const willMarketingPlanBeFree =
    hasFreeMarketingPlan ||
    hasFreePendingMarketingPlan ||
    hasFreeSelectedMarketingPlan;

  const canChooseFreeCorePlan =
    willAutomationPlanBeFree && willMarketingPlanBeFree;

  const pendingEffectiveDate = useMemo(() => {
    const pendingStartDate = (plan?: CurrentlyActivePlan) =>
      plan?.start_date || '';
    const renewalDate = (plan?: CurrentlyActivePlan) =>
      plan?.renewal_date || '';

    return (
      pendingStartDate(pendingGeneralPlan) ||
      pendingStartDate(pendingAutomationPlan) ||
      pendingStartDate(pendingMarketingPlan) ||
      renewalDate(currentGeneralPlan) ||
      renewalDate(currentAutomationPlan) ||
      renewalDate(currentMarketingPlan) ||
      ''
    );
  }, [
    currentGeneralPlan,
    currentAutomationPlan,
    currentMarketingPlan,
    pendingGeneralPlan,
    pendingAutomationPlan,
    pendingMarketingPlan,
  ]);

  const upcomingPendingCorePlan =
    pendingCorePlatformResolvedPlan ?? activeCorePlatformPlan;
  const upcomingPendingAutomationPlan =
    pendingAutomationResolvedPlan ?? activeAutomationPlan;
  const upcomingPendingMarketingPlan =
    pendingMarketingResolvedPlan ?? activeMarketingPlan;

  // The pending/upcoming summary must reflect the pending plan's own seat count,
  // not whatever the user is currently editing in the form.
  const pendingNumUsers =
    (upcomingPendingCorePlan?.config?.limits as any)?.max_users ??
    currentNumUsers;

  const pendingPlanDiffers =
    (pendingGeneralPlan != null &&
      !arePlansEqual(pendingCorePlatformResolvedPlan, currentGeneralPlan)) ||
    (pendingAutomationPlan != null &&
      !arePlansEqual(pendingAutomationResolvedPlan, currentAutomationPlan)) ||
    (pendingMarketingPlan != null &&
      !arePlansEqual(pendingMarketingResolvedPlan, currentMarketingPlan));

  const isPendingCadenceOnlyChange =
    pendingCadenceChanges && !pendingPlanDiffers;

  const pendingDisplayGeneralPlan =
    pendingGeneralPlan ??
    (isPendingCadenceOnlyChange ? currentGeneralPlan : undefined);
  const pendingDisplayAutomationPlan =
    pendingAutomationPlan ??
    (isPendingCadenceOnlyChange ? currentAutomationPlan : undefined);
  const pendingDisplayMarketingPlan =
    pendingMarketingPlan ??
    (isPendingCadenceOnlyChange ? currentMarketingPlan : undefined);

  const upcomingPendingHasPaidPlan =
    (upcomingPendingCorePlan?.price?.amount ?? 0) > 0 ||
    (upcomingPendingAutomationPlan?.price?.amount ?? 0) > 0 ||
    (upcomingPendingMarketingPlan?.price?.amount ?? 0) > 0;

  const pendingOppositeCadenceTotal = computeCadenceAmounts(
    pendingOppositeCadence,
    (upcomingPendingCorePlan?.price?.amount ?? 0) * pendingNumUsers,
    planDiscount(upcomingPendingCorePlan),
    upcomingPendingAutomationPlan?.price?.amount ?? 0,
    planDiscount(upcomingPendingAutomationPlan),
    upcomingPendingMarketingPlan?.price?.amount ?? 0,
    planDiscount(upcomingPendingMarketingPlan)
  ).total;

  const pendingCadenceChangePreview = {
    monthly_cost_summary: { total: pendingOppositeCadenceTotal },
    upgrade_cost_summary: { total: 0 },
  };

  // Cancelling a pending change and switching cadence always takes effect at
  // the next renewal — never immediate, never itself a pending downgrade.
  const pendingCadenceChangeClarification = pendingEffectiveDate
    ? buildPlanChangeClarification(
        {
          plan_change_immediately: false,
          has_pending_downgrades: false,
          is_cadence_change: true,
          new_billing_cycle: pendingOppositeCadence,
          new_billing_cycle_start_date: pendingEffectiveDate,
        },
        t
      )
    : undefined;

  const handleCancelAllPending = () => {
    setPendingPlanDowngradeCancellations(pendingPlanIds);
    showConfirmCancelDowngrade();
  };

  if (isLoading) {
    return <Loader loading />;
  }

  return (
    <PageSizing>
      <PageWrapper>
        <div className="grid grid-cols-[2.5fr_1fr] gap-20 pb-20">
          <div>
            <ContentCard>
              <div>
                <PlanRow
                  ref={generalPlanRowRef}
                  plans={corePlatformPlans}
                  title={t('Kizen Native AI Platform')}
                  period="mo"
                  unit="usr"
                  titleAccessory={
                    showAnnualBilling ? (
                      <Switch
                        dataQa="show-annual-pricing-toggle"
                        label={t('Show Annual Pricing')}
                        checked={showAnnualPricing}
                        textPlacement="left"
                        removeMargin
                        removeLabelMargin
                        onChange={(ev) =>
                          setShowAnnualPricing(ev.target.checked)
                        }
                      />
                    ) : undefined
                  }
                  currentPlan={currentGeneralPlan}
                  selectedPlanIndex={currentSelectedGeneralPlanIndex}
                  setSelectedPlanIndex={handleSelectGeneralPlanIndex}
                  numberInput
                  numberInputLabel={t('Total Platform Users')}
                  numberInputValue={numUsersForCalculation}
                  onNumberInputValueChange={setChosenUserCount}
                  numberInputDisabled={pendingRecalculation}
                  pendingDowngradePlan={pendingDisplayGeneralPlan}
                  onCancelDowngrade={(id) => {
                    setPendingPlanDowngradeCancellations(
                      pendingCadenceChanges ? pendingPlanIds : [id]
                    );
                    showConfirmCancelDowngrade();
                  }}
                  downgradeDate={
                    pendingDisplayGeneralPlan ? pendingEffectiveDate : undefined
                  }
                  freePlanSelectable={canChooseFreeCorePlan}
                  showAnnualPricing={effectiveShowAnnualPricing}
                  isCorePlan
                />
                <Spacer size={20} />
                <div className="flex justify-center">
                  <Button
                    variant="text"
                    color="secondary"
                    rightIcon={
                      showComparisonTable
                        ? 'custom-chevron-up'
                        : 'custom-chevron-down'
                    }
                    maxWidth={false}
                    size="small"
                    onClick={() => {
                      setShowComparisonTable((prev) => !prev);
                    }}
                  >
                    {t('See Complete Features Comparison')}
                  </Button>
                </div>
                {showComparisonTable ? (
                  <Comparison
                    plans={corePlatformPlans}
                    labels={licenseTemplates?.feature_labels ?? {}}
                    labelKey="general"
                  />
                ) : null}
                <Spacer size={30} />
                <hr className="my-0 mx-24" />
                <Spacer size={30} />
                <PlanRow
                  plans={automationsPlans}
                  title="Agentic Workflows"
                  period="mo"
                  currentPlan={currentAutomationPlan}
                  selectedPlanIndex={currentSelectedAutomationPlanIndex}
                  setSelectedPlanIndex={setCurrentSelectedAutomationPlanIndex}
                  selectable={isCorePlanPaid}
                  scrollToGeneralPlanRow={scrollToGeneralPlanRow}
                  pendingDowngradePlan={pendingDisplayAutomationPlan}
                  onCancelDowngrade={(id) => {
                    setPendingPlanDowngradeCancellations(
                      pendingCadenceChanges ? pendingPlanIds : [id]
                    );
                    showConfirmCancelDowngrade();
                  }}
                  downgradeDate={
                    pendingDisplayAutomationPlan
                      ? pendingEffectiveDate
                      : undefined
                  }
                  showAnnualPricing={effectiveShowAnnualPricing}
                />
                {showAutomationEstimator ? (
                  <>
                    <Spacer size={20} />
                    <Estimator
                      period={t('Monthly')}
                      periodAbbreviated={t('mo')}
                      entityPlural={t('Tasks')}
                      start={
                        currentSelectedAutomationPlan?.limits
                          .monthly_tasks_limit
                      }
                      /*
                        When the next plan is not self-serve, the max will be 2,000,000. For now,
                        this is not backend-driven.
                      */
                      end={
                        nextSelectedAutomationPlan?.limits
                          .monthly_tasks_limit ?? 2_000_000
                      }
                      unitSize={
                        currentSelectedAutomationPlan?.overage.unit_size
                      }
                      unitPrice={
                        currentSelectedAutomationPlan?.overage.price_per_unit
                      }
                      cta={
                        nextSelectedAutomationPlan ? (
                          <Typography weight="semibold">
                            {t('Upgrade to {{planName}}', {
                              planName: nextSelectedAutomationPlan.name,
                            })}
                          </Typography>
                        ) : (
                          <Typography
                            variant="link"
                            href="https://kizen.com/contact-sales"
                            target="_blank"
                            weight="semibold"
                          >
                            {t('Contact Sales')}
                          </Typography>
                        )
                      }
                      isMax={!nextSelectedAutomationPlan}
                    />
                  </>
                ) : null}

                <Spacer size={30} />
                <hr className="my-0 mx-24" />
                <Spacer size={30} />
                <PlanRow
                  plans={marketingPlans}
                  title="Marketing"
                  period="mo"
                  currentPlan={currentMarketingPlan}
                  selectedPlanIndex={currentSelectedMarketingPlanIndex}
                  setSelectedPlanIndex={setCurrentSelectedMarketingPlanIndex}
                  selectable={isCorePlanPaid}
                  scrollToGeneralPlanRow={scrollToGeneralPlanRow}
                  pendingDowngradePlan={pendingDisplayMarketingPlan}
                  onCancelDowngrade={(id) => {
                    setPendingPlanDowngradeCancellations(
                      pendingCadenceChanges ? pendingPlanIds : [id]
                    );
                    showConfirmCancelDowngrade();
                  }}
                  downgradeDate={
                    pendingDisplayMarketingPlan
                      ? pendingEffectiveDate
                      : undefined
                  }
                  showAnnualPricing={effectiveShowAnnualPricing}
                />
                {showMarketingEstimator ? (
                  <>
                    <Spacer size={20} />
                    <Estimator
                      period={t('Monthly')}
                      periodAbbreviated={t('mo')}
                      entityPlural={t('Emails')}
                      start={
                        currentSelectedMarketingPlan?.limits
                          .monthly_emails_limit
                      }
                      /*
                        When the next plan is not self-serve, the max will be 1,000,000. For now,
                        this is not backend-driven.
                      */
                      end={
                        nextSelectedMarketingPlan?.limits
                          .monthly_emails_limit ?? 1_000_000
                      }
                      unitSize={currentSelectedMarketingPlan?.overage.unit_size}
                      unitPrice={
                        currentSelectedMarketingPlan?.overage.price_per_unit
                      }
                      cta={
                        nextSelectedMarketingPlan ? (
                          <Typography weight="semibold">
                            {t('Upgrade to {{planName}}', {
                              planName: nextSelectedMarketingPlan.name,
                            })}
                          </Typography>
                        ) : (
                          <Typography
                            variant="link"
                            href="https://kizen.com/contact-sales"
                            target="_blank"
                            weight="semibold"
                          >
                            {t('Contact Sales')}
                          </Typography>
                        )
                      }
                      isMax={!nextSelectedMarketingPlan}
                    />
                  </>
                ) : null}
              </div>
            </ContentCard>
          </div>
          <div>
            <div className="sticky top-6 gap-20 flex flex-col">
              <PlanSummary
                currentSelectedCorePlatformPlan={
                  currentSelectedCorePlatformPlan
                }
                currentSelectedAutomationPlan={currentSelectedAutomationPlan}
                currentSelectedMarketingPlan={currentSelectedMarketingPlan}
                activeCorePlatformPlan={activeCorePlatformPlan}
                activeAutomationPlan={activeAutomationPlan}
                activeMarketingPlan={activeMarketingPlan}
                numUsersForCalculation={numUsersForCalculation}
                currentNumUsersForCalculation={currentNumUsers}
                pendingNumUsersForCalculation={pendingNumUsers}
                annualDiscountPercentage={annualDiscountPercentage}
                showAnnualBilling={showAnnualBilling}
                currentCadence={currentCadence}
                upcomingCadence={upcomingCadence}
                onUpcomingCadenceChange={handleUpcomingCadenceChange}
                showUpcoming={showUpcoming}
                canSwitchCadenceOnly={canSwitchCadenceOnly}
                onRequestCadenceChange={handleRequestCadenceChange}
                planChangeClarification={planChangeClarification}
                newBillingCycleStartDate={
                  billingPreviewResponse?.new_billing_cycle_start_date
                }
                proratedAmount={
                  billingPreviewResponse?.upgrade_cost_summary?.total != null
                    ? Number(billingPreviewResponse.upgrade_cost_summary.total)
                    : null
                }
                summaryTotal={
                  billingPreviewResponse?.monthly_cost_summary?.total != null
                    ? Number(billingPreviewResponse.monthly_cost_summary.total)
                    : null
                }
                previewedCadence={previewedCadence}
                isDirty={isDirty}
                pendingRecalculation={pendingRecalculation}
                isAwaitingPreview={isAwaitingPreview}
                onCancelUpdate={handleCancelUpdate}
                onUpdatePlan={clickSaveChanges}
                hasPendingChange={hasAnyPendingPlans}
                pendingCorePlatformPlan={upcomingPendingCorePlan}
                pendingAutomationPlan={upcomingPendingAutomationPlan}
                pendingMarketingPlan={upcomingPendingMarketingPlan}
                pendingPlanDiffers={pendingPlanDiffers}
                upcomingPendingHasPaidPlan={upcomingPendingHasPaidPlan}
                onCancelPendingPlan={handleCancelAllPending}
                onChangePendingBillingCadence={showConfirmPendingCadenceChange}
                pendingEffectiveDate={pendingEffectiveDate}
                pendingCadence={pendingCadence}
              />
            </div>
          </div>
        </div>
      </PageWrapper>
      <ConfirmPlanChangesDialog
        modalProps={confirmSaveChangesModalProps}
        billingPreviewResponse={billingPreviewResponse}
        planChangeClarification={planChangeClarification}
        cadence={upcomingCadence}
      />
      <DowngradeCancellationDialog
        {...cancelDowngradeModalProps}
        currentCadence={currentCadence}
      />
      <ConfirmPlanChangesDialog
        modalProps={confirmPendingCadenceChangeModalProps}
        billingPreviewResponse={pendingCadenceChangePreview}
        planChangeClarification={pendingCadenceChangeClarification}
        cadence={pendingOppositeCadence}
      />
      <PendingPlanChangesDialog {...lockedPlanCategoryModalProps} />
      <UserCountErrorDialog {...invalidUserCountModalProps} />
      <MissingBillingDetailsDialog {...missingBillingInfoModalProps} />
      {showAddCardModal ? (
        <AddCardModal
          show
          onHide={handleAddCardModalHide}
          existingIds={[]}
          onSuccess={handleCardAdded}
          showMakePrimary={false}
        />
      ) : null}
      <CannotChangeBillingCadenceDialog {...cannotChangeCadenceModalProps} />
      <RequiresPaidPlanToCancelDowngrade {...cannotCancelDowngradeModalProps} />
      <ConfirmNavigationModal when={isDirty} />
    </PageSizing>
  );
};
